import { useQueryClient } from '@tanstack/react-query'
import {
  type ConversationData,
  type ConversationFeedbackAddInput,
  type ConversationRemoveInput,
  type UserId,
} from '@terros/common'
import { conversationKey, conversationsKey } from './keys'

export function useConversationCache() {
  const queryClient = useQueryClient()

  return {
    setItem: (conversation: ConversationData) => {
      queryClient.setQueryData<ConversationData>(conversationKey(conversation.convoId), conversation)
      queryClient.setQueriesData<ConversationData[]>(
        { queryKey: conversationsKey({ userId: conversation.ownerId }) },
        (existing) => {
          if (!existing) return existing
          return existing.map((c) => (c.convoId === conversation.convoId ? conversation : c))
        }
      )
    },
    addItem: (conversation: ConversationData) => {
      queryClient.setQueryData<ConversationData>(conversationKey(conversation.convoId), conversation)

      queryClient.setQueriesData<ConversationData[]>(
        { queryKey: conversationsKey({ userId: conversation.ownerId }) },
        (existing) => {
          if (!existing) return [conversation]
          return [...existing, conversation]
        }
      )
    },
    addFeedback: (updated: ConversationFeedbackAddInput) => {
      queryClient.setQueryData<ConversationData>(conversationKey(updated.conversationId), (existing) => {
        if (!existing) return
        const feedback = existing.feedback ? [...existing.feedback, updated.feedback] : [updated.feedback]
        return { ...existing, feedback }
      })
    },
    clearItem: (input: ConversationRemoveInput) => {
      queryClient.setQueriesData<ConversationData[]>({ queryKey: conversationsKey() }, (existing) => {
        if (!existing) return
        return existing.filter((c) => c.convoId !== input.conversationId)
      })

      queryClient.removeQueries({ queryKey: conversationKey(input.conversationId) })
    },
    clearList: (userId?: UserId) => {
      queryClient.removeQueries({ queryKey: conversationsKey({ userId }) })
    },
  }
}
